import { useState, useEffect } from 'react';
import { BrowserRouter, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from './components/ThemeProvider';
import { LocalizationProvider } from './contexts/LocalizationContext';
import { StoreSettingsProvider, useStoreSettings } from './contexts/StoreSettingsContext';
import { AppRoutes } from './routes';
import { routes, publicRoutes } from './config';
import { cleanupAllRecaptcha } from './lib/firebase';
import type { RegisterData } from './types';
import type { ConfirmationResult } from 'firebase/auth';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AuthProvider, useAuth } from './auth/AuthContext';

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { customer, firebaseUser, isAuthenticated, isLoading } = useAuth();
  const { storeSettings } = useStoreSettings();
  const [confirmationResult] = useState<ConfirmationResult | null>(null);
  const [registerFormData] = useState<RegisterData>({
    firstName: '',
    lastName: '',
    dateOfBirth: { year: '', month: '', day: '' },
    gender: '',
    referralCode: '',
  });
  const [hasHandledInitialRoute, setHasHandledInitialRoute] = useState(false);

  // Cleanup reCAPTCHA on component unmount
  useEffect(() => {
    return () => {
      cleanupAllRecaptcha();
    };
  }, []);

  // Handle authentication state changes - only on first load
  useEffect(() => {
    // Don't redirect while authentication state is still loading
    if (isLoading) {
      return;
    }

    // Only handle initial route once
    if (hasHandledInitialRoute) {
      return;
    }

    // Normalize path by removing trailing slash
    const currentPath = location.pathname.replace(/\/$/, '');
    const isPublic = publicRoutes.includes(currentPath as any);

    console.log('App.tsx: Handling initial route:', currentPath);
    console.log('App.tsx: isPublic:', isPublic);
    console.log('App.tsx: isAuthenticated:', isAuthenticated);
    console.log('App.tsx: customer:', customer);
    console.log('App.tsx: firebaseUser:', firebaseUser);

    // Skip handling for specific routes that should handle their own navigation
    if (currentPath === routes.registerSuccess ||
        currentPath === routes.otp ||
        currentPath === routes.register ||
        currentPath === routes.addToWallet) {
      console.log('App.tsx: Skipping route handling for:', currentPath);
      setHasHandledInitialRoute(true);
      return;
    }

    // Check if user is fully authenticated (has both Firebase auth and customer data)
    const isFullyAuthenticated = isAuthenticated && customer && firebaseUser;

    // Only redirect if we're not already on the correct page
    if (isFullyAuthenticated) {
      // User is fully authenticated - redirect to home if on public routes
      if (isPublic && currentPath !== routes.home) {
        console.log('App.tsx: User fully authenticated, redirecting to home');
        navigate(routes.home, { replace: true });
      } else {
        // User fully authenticated and on protected route - allowing access
        console.log('App.tsx: User fully authenticated, allowing access to:', currentPath);
      }
    } else {
      // User is not authenticated - redirect to login if on protected routes
      if (!isPublic && currentPath !== routes.login) {
        console.log('App.tsx: User not authenticated, redirecting to login');
        navigate(routes.login, { replace: true });
      } else {
        // User not authenticated but on public route - allowing access
        console.log('App.tsx: User not authenticated, allowing access to public route:', currentPath);
      }
    }

    // Mark as handled
    setHasHandledInitialRoute(true);
  }, [isAuthenticated, isLoading, location.pathname, navigate, customer, firebaseUser, hasHandledInitialRoute]);

  // Show loading screen while authentication state is being determined
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          background: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url("${storeSettings?.backgroundUrl || '/images/background.png'}")`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <img src={storeSettings?.logoUrl || '/icons/logo.svg'} alt="Store Logo" style={{ height: '50px', width: 'auto' }} />
        </Box>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 3,
            color: 'white',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            fontSize: '32px',
          }}
        >
          {storeSettings?.text || 'GRINDIE'}
        </Typography>
        <CircularProgress
          size={40}
          sx={{
            color: 'white',
            mb: 2,
          }}
        />
        <Typography
          variant="body1"
          sx={{
            color: 'white',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            opacity: 0.9,
          }}
        >
          Loading...
        </Typography>
      </Box>
    );
  }

  const handleBack = () => {
    if (location.pathname === routes.referralCode) {
      navigate(routes.register);
    } else {
      navigate(-1);
    }
  };

  const handleSwitchToRegister = () => {
    navigate(routes.register);
  };

  const handleBackToLogin = () => {
    navigate(routes.login);
  };

  return (

      <AppRoutes
        currentUser={customer}
        confirmationResult={confirmationResult}
        registerFormData={registerFormData}
        firebaseUser={firebaseUser}
        onBack={handleBack}
        onSwitchToRegister={handleSwitchToRegister}
        onBackToLogin={handleBackToLogin}
      />

  );
}

export default function App() {
  return (
    <AuthProvider>
      <StoreSettingsProvider>
        <ThemeProvider>
          <LocalizationProvider>
            <BrowserRouter>
              <AppContent />
            </BrowserRouter>
          </LocalizationProvider>
        </ThemeProvider>
      </StoreSettingsProvider>
    </AuthProvider>
  );
} 